<script setup lang="ts">
import { onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCommentsStore } from '../store'
import { useUsersStore } from '@/views/Users/store'
import type { Comment, CommentEntityType } from '@/types'

import CommentItem from './CommentItem.vue'
import CommentForm from './CommentForm.vue'

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollArea } from '@/components/ui/scroll-area'
import { MessageSquare } from 'lucide-vue-next'

const props = defineProps<{
  entityType: CommentEntityType
  entityId: string
  title?: string
  maxHeight?: string
}>()

const { t } = useI18n()
const commentsStore = useCommentsStore()
const usersStore = useUsersStore()

onMounted(async () => {
  if (usersStore.users.length === 0) {
    await usersStore.fetchUsers()
  }
  await commentsStore.fetchComments(props.entityType, props.entityId)
})

watch(
  () => [props.entityType, props.entityId],
  async ([newType, newId]) => {
    if (newType && newId) {
      await commentsStore.fetchComments(newType as CommentEntityType, newId as string)
    }
  }
)

const handleAddComment = async (content: string, isInternal: boolean) => {
  await commentsStore.createComment({
    entityType: props.entityType,
    entityId: props.entityId,
    content,
    isInternal,
  })
}

const handleEditComment = async (comment: Comment, content: string) => {
  await commentsStore.updateComment(comment.id, { content })
}

const handleDeleteComment = async (comment: Comment) => {
  await commentsStore.deleteComment(comment.id)
}
</script>

<template>
  <Card>
    <CardHeader class="pb-3">
      <CardTitle class="flex items-center gap-2 text-base">
        <MessageSquare class="h-4 w-4" />
        {{ title || t('comments.title') }}
        <span v-if="commentsStore.comments.length > 0" class="text-muted-foreground font-normal">
          ({{ commentsStore.comments.length }})
        </span>
      </CardTitle>
    </CardHeader>
    <CardContent class="space-y-4">
      <!-- Comment Form -->
      <CommentForm
        :is-loading="commentsStore.isLoading"
        @submit="handleAddComment"
      />

      <Separator />

      <!-- Loading State -->
      <div v-if="commentsStore.isLoading" class="space-y-4">
        <div class="flex gap-3">
          <Skeleton class="h-8 w-8 rounded-full" />
          <div class="flex-1 space-y-2">
            <Skeleton class="h-4 w-32" />
            <Skeleton class="h-16 w-full" />
          </div>
        </div>
        <div class="flex gap-3">
          <Skeleton class="h-8 w-8 rounded-full" />
          <div class="flex-1 space-y-2">
            <Skeleton class="h-4 w-24" />
            <Skeleton class="h-12 w-3/4" />
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="commentsStore.comments.length === 0"
        class="flex flex-col items-center justify-center py-8 text-center"
      >
        <MessageSquare class="h-10 w-10 text-muted-foreground mb-3" />
        <p class="text-sm text-muted-foreground">{{ t('comments.noComments') }}</p>
        <p class="text-xs text-muted-foreground mt-1">{{ t('comments.beFirst') }}</p>
      </div>

      <!-- Comments List -->
      <ScrollArea v-else :class="maxHeight ? `h-[${maxHeight}]` : ''" :style="maxHeight ? { maxHeight } : {}">
        <div class="divide-y">
          <CommentItem
            v-for="comment in commentsStore.comments"
            :key="comment.id"
            :comment="comment"
            @edit="handleEditComment"
            @delete="handleDeleteComment"
          />
        </div>
      </ScrollArea>
    </CardContent>
  </Card>
</template>
